// 押注金額網格浮層 — 對照 Unity `SlotFramework/Prefab/UI/Base/BetListWindow.prefab`
// ＋ `BetListWindowManager.cs`（Combo skin，`SkinType.Combo`=0；UFA／LLG 是另外兩套皮膚，本作不用）。
//
// ⚠️ 這**不是彈窗**：Unity 版沒有全螢幕 Mask／暗底，也沒有「點面板外關閉」的邏輯
//    （整個 prefab 只有 `Bg`＋`Buttons` 兩個子節點）。它是掛在押注鈕（`Bet` 節點，
//    `BottomBarUI.SetBetListWindow` 強制 `anchoredPosition = Vector2.zero`）上方的浮層，
//    由 `UIBetClickEvent` **切換**開關。先前本移植做成置中的 ModalDialog（暗底＋標題＋自繪圓角格）
//    與 Unity 完全不同，故整個換掉（同 MenuPopup 的處置）。
//
// 版面（Unity 設計 px，y 向上、原點＝押注鈕中心）：
//   Bg      aPos (0, 265) sizeDelta 542×442（pic_betBg 原生尺寸；spriteBorder 全 0 ⇒ 純拉伸）
//           ⇒ 下緣 y=+44，底圖最下緣中央的三角箭頭尖端就落在此處（押注鈕上緣 42 一帶）
//           執行期寬改為 `欄數 × columnWidth(181) + _horizontalEdgeBleed(10)`，高不變
//   Buttons aPos (0, 280)（＝`_btnCreateRoot`，格子全部由 script Instantiate 進來）
//   格子    BetBtnPrefab 188×92；`Text` 子節點 150×50、HorizontalLayoutGroup MiddleCenter
//           ＋ChildForceExpandWidth ⇒ ₱ 貼左緣、金額貼右緣（見 CELL_TEXT_W）
//
// 排版公式（`BetListWindowManager.CreateBtns`，序列化值見常數）：
//   欄數 = ceil(檔數 / 5)（`KeepColumnCount` 只有 UFA 皮膚才 >0，Combo 為 0 故不參與）
//   offsetCount = (欄數 − 1) × 0.5；beginX = _xBegin + offsetCount × _xOffset
//   第 col 欄 x = beginX − col × _xOffset  ⇒ **col 0 在最右邊**
//   第 i 格 y = _yBegin + i × _yOffset     ⇒ **i 0 在最下面**（y 向上）
//   ⇒ index 0（最小注）在右下角、最大注在左上角。這是 Unity 的排列，勿改成一般的左上→右下。
//   欄數不足時仍會建滿 `欄數 × 5` 格，多出來的是**不可點的空格**（底圖照舊、文字清空）。
//
// 選中態只換底圖（`buttonOnSprite`／`buttonOffSprite`），Unity **不改文字顏色／字級**。
//
// [已知差異] ①Unity `Bg` 掛 FrostedGlass 材質（`_BlurSize 5` 取樣背後畫面模糊），但其 `_ImageAlpha=1`
//   使 `blurMask = 貼圖 alpha`，在面板不透明處幾乎完全採用貼圖本色、模糊僅作用於半透明邊緣，
//   故直接貼圖即可（同 SystemDialog 對該材質的處置）。②Unity prefab 的 `m_LocalScale` 是 1 而
//   `m_Alpha` 是 0，導致**第一次**開窗只有淡入、沒有 0→1 縮放（關窗才縮到 0，第二次起才有）；
//   本移植一律 0→1，視為來源未初始化的副作用而非設計意圖。

import { Container, Sprite, Text } from 'pixi.js'
import { gsap } from 'gsap'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { formatMoney } from '../../../framework/core/format'
import { getCurrencySymbol } from '../../../framework/core/currency'
import { fontFamilyForLocale } from '../../../framework/i18n/i18n'
import type { Orientation, Resizable } from '../../../framework/render/design'
import { getUiTexture } from '../../assets/UiTextures'

// 幣別符號改由 getCurrencySymbol() 取（Unity BetListWindowManager 以 GetCurrencyText 取符號、
// 不改文字顏色，故格內文字色保持 prefab 值 #FFE8C2）。

// ── BetListWindow.prefab／BetListWindowManager 序列化值 ──
const BG_AY = 265
const BG_H = 442
const BUTTONS_AY = 280
const COLUMN_WIDTH = 181
const EDGE_BLEED = 10
const ITEMS_PER_COLUMN = 5
const X_BEGIN = 0
const Y_BEGIN = -155.9
const X_OFFSET = 181
const Y_OFFSET = 79

// ── BetBtnPrefab.prefab 序列化值 ──
const CELL_W = 188
const CELL_H = 92
/** `Text` 節點寬（₱ 與金額分別貼其左右緣）。 */
const CELL_TEXT_W = 150
/** 兩個 TMP 共用：`m_fontSize` 36、autoSize 開（ValueText `m_fontSizeMin` 18）、`m_fontColor` #FFE8C2。 */
const FONT_SIZE = 36
const FONT_SIZE_MIN = 18
const TEXT_COLOR = 0xffe8c2

/** Button `m_Transition: ColorTint` 的三態色與 disabled alpha（`m_FadeDuration` 0.1 未做逐格淡變）。 */
const TINT_NORMAL = 0xffffff
const TINT_HOVER = 0xf5f5f5
const TINT_PRESSED = 0xc8c8c8
const DISABLED_ALPHA = 0.5019608

/** `PlayAnimation` 的 `const float duration = 0.2f`（DOFade＋DOScale，未指定 ease ⇒ DOTween 預設 OutQuad）。 */
const TWEEN_S = 0.2

/** 押注鈕（Unity `Bet` 節點）中心的螢幕座標與「設計 px → 螢幕 px」縮放（由 BottomBarView 提供）。 */
export type BetAnchor = () => { readonly x: number; readonly y: number; readonly scale: number }

/**
 * 單格金額鈕（Unity BetBtnPrefab）：底圖換圖表示選中，₱ 與金額分貼 `Text` 節點左右緣。
 * 空格（欄數補齊用）不可點、文字清空，底圖仍是未選中圖——與 Unity 一致。
 */
class BetCell extends Container {
  private readonly bg = new Sprite(getUiTexture('betCellOff'))
  private readonly currency = cellText(0)
  private readonly value = cellText(1)
  private empty = true

  constructor(onTap: () => void) {
    super()
    this.bg.anchor.set(0.5)
    this.addChild(this.bg, this.currency, this.value)
    this.eventMode = 'static'
    this.on('pointertap', () => {
      if (!this.empty) onTap()
    })
    this.on('pointerover', () => this.applyTint(TINT_HOVER))
    this.on('pointerout', () => this.applyTint(TINT_NORMAL))
    this.on('pointerdown', () => this.applyTint(TINT_PRESSED))
    this.on('pointerup', () => this.applyTint(TINT_HOVER))
    this.on('pointerupoutside', () => this.applyTint(TINT_NORMAL))
  }

  /** ⚠️ 不可命名為 `tint`：`Container` 已內建 `tint: number`（另一條同類坑是 `label`，見 SystemDialog）。 */
  private applyTint(color: number): void {
    if (!this.empty) this.bg.tint = color
  }

  /** 設定金額；`undefined` ＝ 欄數補齊用的空格。 */
  setValue(amount: number | undefined): void {
    this.empty = amount === undefined
    this.currency.text = this.empty ? '' : getCurrencySymbol()
    this.value.text = this.empty ? '' : formatMoney(amount!)
    this.cursor = this.empty ? 'default' : 'pointer'
    this.alpha = this.empty ? DISABLED_ALPHA : 1
    this.bg.tint = TINT_NORMAL
  }

  setSelected(selected: boolean): void {
    this.bg.texture = getUiTexture(selected ? 'betCellOn' : 'betCellOff')
  }

  /** 依設計→螢幕縮放排版；文字超出 `Text` 節點寬時等比縮字（TMP autoSize 等價，下限 fontSizeMin）。 */
  layout(s: number): void {
    this.bg.width = CELL_W * s
    this.bg.height = CELL_H * s

    const half = (CELL_TEXT_W * s) / 2
    let fontSize = FONT_SIZE * s
    this.currency.style.fontSize = fontSize
    this.value.style.fontSize = fontSize
    const needed = this.currency.width + this.value.width
    const room = CELL_TEXT_W * s
    if (needed > room) {
      fontSize = Math.max(FONT_SIZE_MIN * s, (fontSize * room) / needed)
      this.currency.style.fontSize = fontSize
      this.value.style.fontSize = fontSize
    }
    this.currency.position.set(-half, 0)
    this.value.position.set(half, 0)
  }
}

export class BetListPopup extends Container implements Resizable {
  private readonly bg = new Sprite(getUiTexture('betListBg'))
  private readonly cells: BetCell[] = []
  private options: readonly number[] = []
  private currentIndex = 0
  /** 開關狀態（與 `visible` 分開：淡出動畫期間仍 visible）。 */
  private opened = false

  constructor(
    private readonly bus: EventBus<GameEventMap>,
    private readonly anchor: BetAnchor,
  ) {
    super()
    this.visible = false
    this.alpha = 0
    this.scale.set(0)
    this.bg.anchor.set(0.5)
    this.addChild(this.bg)

    this.bus.on('betConfigChanged', ({ options, index }) => {
      const changed = !sameNumbers(this.options, options)
      this.options = options
      this.currentIndex = index
      if (changed) this.rebuild()
      else this.updateHighlight()
      if (this.opened) this.layout()
    })
    // 高亮跟隨「實際生效的押注」而非本浮層自己的點擊：購買獎勵彈窗的 ± 也會改押注（同樣走 betSelected →
    // GameController → betChanged），若只在自己被點時更新，從別處改注後再開窗就會亮錯格。
    // betChanged 只帶金額不帶 index，故以金額回查（押注檔位不重複）。
    this.bus.on('betChanged', ({ bet }) => {
      const index = this.options.indexOf(bet)
      if (index < 0) return
      this.currentIndex = index
      this.updateHighlight()
    })
    // 押注鈕／金額牌 = 切換開關（對應 UIBetClickEvent → OnShow → `isActive = !interactable`）。
    this.bus.on('betMenuClicked', () => this.toggle())
    // 關閉（對應 SpinTriggerEvent／UIAutoClickEvent／UIMenuClickEvent／InfoUIActiveEvent）。
    for (const event of ['spinRequested', 'autoSpinClicked', 'menuClicked', 'infoClicked'] as const) {
      this.bus.on(event, () => this.close())
    }
    // 對應 UIBottomLockEvent（開轉後底部列上鎖）。
    this.bus.on('spinStateChanged', ({ spinning }) => {
      if (spinning) this.close()
    })
  }

  /** 依檔位數重建格子：Unity 每欄固定建滿 5 格，超出檔數的是空格。 */
  private rebuild(): void {
    for (const cell of this.cells) cell.destroy()
    this.cells.length = 0
    const total = this.columnCount * ITEMS_PER_COLUMN
    for (let index = 0; index < total; index += 1) {
      const cell = new BetCell(() => this.pick(index))
      cell.setValue(this.options[index])
      this.cells.push(cell)
      this.addChild(cell)
    }
    this.updateHighlight()
  }

  private get columnCount(): number {
    return Math.ceil(this.options.length / ITEMS_PER_COLUMN)
  }

  private pick(index: number): void {
    this.close()
    this.bus.emit('betSelected', { index })
  }

  private updateHighlight(): void {
    this.cells.forEach((cell, index) => cell.setSelected(index === this.currentIndex))
  }

  private toggle(): void {
    if (this.opened) this.close()
    else this.open()
  }

  private open(): void {
    if (this.opened) return
    this.opened = true
    this.visible = true
    this.layout()
    gsap.killTweensOf(this)
    gsap.killTweensOf(this.scale)
    gsap.to(this, { alpha: 1, duration: TWEEN_S, ease: 'power2.out' })
    gsap.to(this.scale, { x: 1, y: 1, duration: TWEEN_S, ease: 'power2.out' })
  }

  private close(): void {
    if (!this.opened) return
    this.opened = false
    gsap.killTweensOf(this)
    gsap.killTweensOf(this.scale)
    gsap.to(this, { alpha: 0, duration: TWEEN_S, ease: 'power2.out' })
    gsap.to(this.scale, {
      x: 0,
      y: 0,
      duration: TWEEN_S,
      ease: 'power2.out',
      onComplete: () => {
        if (!this.opened) this.visible = false
      },
    })
  }

  resize(_width: number, _height: number, _orientation: Orientation): void {
    if (this.opened) this.layout()
  }

  /**
   * 排版：容器原點設在押注鈕中心（＝Unity root 的位置與 pivot），子節點用相對設計座標，
   * 這樣開關窗的縮放軸心自然落在押注鈕上，視覺上就是「從金額牌彈出」（同 Unity）。
   */
  private layout(): void {
    const { x, y, scale: s } = this.anchor()
    if (s <= 0) return
    this.position.set(x, y)

    const columns = this.columnCount
    this.bg.width = (columns * COLUMN_WIDTH + EDGE_BLEED) * s
    this.bg.height = BG_H * s
    this.bg.position.set(0, -BG_AY * s)

    // 欄由右往左、格由下往上（見檔頭公式）。
    const beginX = X_BEGIN + (columns - 1) * 0.5 * X_OFFSET
    this.cells.forEach((cell, index) => {
      const column = Math.floor(index / ITEMS_PER_COLUMN)
      const row = index % ITEMS_PER_COLUMN
      cell.layout(s)
      cell.position.set((beginX - column * X_OFFSET) * s, -(BUTTONS_AY + Y_BEGIN + row * Y_OFFSET) * s)
    })
  }
}

/** 格內文字（ARIAL SDF、`m_fontColor` #FFE8C2、無漸層無描邊）；anchorX 0＝₱ 貼左緣、1＝金額貼右緣。 */
function cellText(anchorX: number): Text {
  const text = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: FONT_SIZE, fill: TEXT_COLOR },
  })
  text.anchor.set(anchorX, 0.5)
  return text
}

/** 逐項比較押注清單是否真的變更（避免無謂重建）。 */
function sameNumbers(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false
  return a.every((value, index) => value === b[index])
}
